/**
 * Audit trail vocabulary. `workspace_audit_logs` rows are written by the API on
 * every workspace mutation; SQL triggers act as a backstop for anything that
 * reaches the database directly.
 */

export const AUDIT_ACTIONS = {
  userSynced: "USER_SYNCED",
  userUpdated: "USER_UPDATED",
  workspaceCreated: "WORKSPACE_CREATED",
  workspaceUpdated: "WORKSPACE_UPDATED",
  workspaceArchived: "WORKSPACE_ARCHIVED",
  memberInvited: "MEMBER_INVITED",
  memberRoleUpdated: "MEMBER_ROLE_UPDATED",
  memberRemoved: "MEMBER_REMOVED",
  memberJoined: "MEMBER_JOINED",
  productCreated: "PRODUCT_CREATED",
  productUpdated: "PRODUCT_UPDATED",
  productDeleted: "PRODUCT_DELETED",
  variantCreated: "VARIANT_CREATED",
  variantUpdated: "VARIANT_UPDATED",
  variantDeleted: "VARIANT_DELETED",
  orderCreated: "ORDER_CREATED",
  orderUpdated: "ORDER_UPDATED",
  orderCancelled: "ORDER_CANCELLED",
  orderReturned: "ORDER_RETURNED",
  stockWrittenOff: "STOCK_WRITTEN_OFF",
  paymentSettled: "PAYMENT_SETTLED",
  invoiceIssued: "INVOICE_ISSUED",
  invoiceRendered: "INVOICE_RENDERED",
  invoiceVoided: "INVOICE_VOIDED",
  invoiceShared: "INVOICE_SHARED",
  settingsUpdated: "SETTINGS_UPDATED",
  storageObjectUploaded: "STORAGE_OBJECT_UPLOADED",
  storageObjectDeleted: "STORAGE_OBJECT_DELETED",
  superboardUserUpdated: "SUPERBOARD_USER_UPDATED",
  superboardSettingsUpdated: "SUPERBOARD_SETTINGS_UPDATED",
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];

export const AUDIT_RESOURCES = {
  appUsers: "app_users",
  workspaces: "workspaces",
  members: "workspace_members",
  products: "products",
  variants: "product_variants",
  bundles: "bundle_items",
  stock: "stock_movements",
  orders: "orders",
  payments: "payments",
  invoices: "invoices",
  settings: "workspace_settings",
  storage: "storage",
  appSettings: "app_settings",
} as const;

export type AuditResource = (typeof AUDIT_RESOURCES)[keyof typeof AUDIT_RESOURCES];

export const AUDIT_RESOURCE_BY_ACTION: Record<AuditAction, AuditResource> = {
  USER_SYNCED: AUDIT_RESOURCES.appUsers,
  USER_UPDATED: AUDIT_RESOURCES.appUsers,
  WORKSPACE_CREATED: AUDIT_RESOURCES.workspaces,
  WORKSPACE_UPDATED: AUDIT_RESOURCES.workspaces,
  WORKSPACE_ARCHIVED: AUDIT_RESOURCES.workspaces,
  MEMBER_INVITED: AUDIT_RESOURCES.members,
  MEMBER_ROLE_UPDATED: AUDIT_RESOURCES.members,
  MEMBER_REMOVED: AUDIT_RESOURCES.members,
  MEMBER_JOINED: AUDIT_RESOURCES.members,
  PRODUCT_CREATED: AUDIT_RESOURCES.products,
  PRODUCT_UPDATED: AUDIT_RESOURCES.products,
  PRODUCT_DELETED: AUDIT_RESOURCES.products,
  VARIANT_CREATED: AUDIT_RESOURCES.variants,
  VARIANT_UPDATED: AUDIT_RESOURCES.variants,
  VARIANT_DELETED: AUDIT_RESOURCES.variants,
  ORDER_CREATED: AUDIT_RESOURCES.orders,
  ORDER_UPDATED: AUDIT_RESOURCES.orders,
  ORDER_CANCELLED: AUDIT_RESOURCES.orders,
  ORDER_RETURNED: AUDIT_RESOURCES.orders,
  STOCK_WRITTEN_OFF: AUDIT_RESOURCES.stock,
  PAYMENT_SETTLED: AUDIT_RESOURCES.payments,
  INVOICE_ISSUED: AUDIT_RESOURCES.invoices,
  INVOICE_RENDERED: AUDIT_RESOURCES.invoices,
  INVOICE_VOIDED: AUDIT_RESOURCES.invoices,
  INVOICE_SHARED: AUDIT_RESOURCES.invoices,
  SETTINGS_UPDATED: AUDIT_RESOURCES.settings,
  STORAGE_OBJECT_UPLOADED: AUDIT_RESOURCES.storage,
  STORAGE_OBJECT_DELETED: AUDIT_RESOURCES.storage,
  SUPERBOARD_USER_UPDATED: AUDIT_RESOURCES.appUsers,
  SUPERBOARD_SETTINGS_UPDATED: AUDIT_RESOURCES.appSettings,
};

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  USER_SYNCED: "Signed in for the first time",
  USER_UPDATED: "Updated profile",
  WORKSPACE_CREATED: "Created workspace",
  WORKSPACE_UPDATED: "Updated workspace",
  WORKSPACE_ARCHIVED: "Archived workspace",
  MEMBER_INVITED: "Invited member",
  MEMBER_ROLE_UPDATED: "Changed member role",
  MEMBER_REMOVED: "Removed member",
  MEMBER_JOINED: "Member joined",
  PRODUCT_CREATED: "Created product",
  PRODUCT_UPDATED: "Updated product",
  PRODUCT_DELETED: "Deleted product",
  VARIANT_CREATED: "Created variant",
  VARIANT_UPDATED: "Updated variant",
  VARIANT_DELETED: "Deleted variant",
  ORDER_CREATED: "Created order",
  ORDER_UPDATED: "Updated order",
  ORDER_CANCELLED: "Cancelled order",
  ORDER_RETURNED: "Returned equipment",
  STOCK_WRITTEN_OFF: "Wrote off damaged units",
  PAYMENT_SETTLED: "Settled payment",
  INVOICE_ISSUED: "Issued invoice",
  INVOICE_RENDERED: "Rendered invoice image",
  INVOICE_VOIDED: "Voided invoice",
  INVOICE_SHARED: "Shared invoice",
  SETTINGS_UPDATED: "Updated settings",
  STORAGE_OBJECT_UPLOADED: "Uploaded file",
  STORAGE_OBJECT_DELETED: "Deleted file",
  SUPERBOARD_USER_UPDATED: "Superboard: updated user",
  SUPERBOARD_SETTINGS_UPDATED: "Superboard: updated settings",
};

export const AUDIT_DEFAULTS = {
  recentLimit: 5,
  pageSize: 25,
  maxPayloadKeys: 40,
} as const;
